import * as amplitude from '@amplitude/analytics-browser';
import {ApiCall} from '@remoov/api';
import {
  AMPLITUDE_KEY,
  base64ToMimeType,
  Constants,
  nodeApiEndpoint,
  parseQuery,
  readExifRotationFromBuffer,
  remoovNodeEndpoint,
  safeParse,
  signInFullUrl,
  throttle,
  uniqueId,
  User,
} from '@remoov/functional';
import {compose, mergeDeepRight} from 'ramda';
import {createElement, Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react';

const {
  NewSiteUrlJs: {SIGN_IN_URL},
} = Constants;

export function uploadSingleFile(mimeTypes: string[] = []): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (mimeTypes.length) input.accept = mimeTypes.join(', ');
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) resolve(file);
      else reject();
    };
    input.click();
  });
}
export function downloadFile(url: string, filename: string): void {
  const {body} = document;
  if (!body) return;
  const element = document.createElement('a');
  element.setAttribute('href', url);
  element.setAttribute('download', filename);
  element.style.display = 'none';
  body.appendChild(element);
  element.click();
  body.removeChild(element);
}
export function clickById(id: string): void {
  const node = document.getElementById(id);
  if (node) node.click();
}
export function apiPost(
  url: string,
  payload: Record<string, unknown>,
  timeout?: number,
): Promise<unknown> {
  return postJson(
    url,
    {
      ...payload,
      current_url: window.location.href,
      user: loadUser(),
    },
    timeout,
  );
}

//@ts-ignore fetch
export const apiCall: ApiCall = async (execute, params, extraArgs) => {
  const tmpUrl = extraArgs?.url?.path
    ? new URL(extraArgs.url.path, remoovNodeEndpoint())
    : new URL(nodeApiEndpoint() + (extraArgs?.files ? '/files' : ''));
  if (extraArgs?.url?.search) {
    Object.entries(extraArgs.url.search).map(([k, v]) => tmpUrl.searchParams.set(k, String(v)));
  }
  if (extraArgs?.files) {
    const body = new FormData();
    const payload = {execute, params, current_url: window.location.href, user: loadUser()};
    body.append('payload', JSON.stringify(payload));
    extraArgs?.files?.forEach((file: File) => body.append('files', file));
    const ab = new AbortController();
    setTimeout(() => ab.abort(), extraArgs?.timeout || 60_000);
    const id = uniqueId();
    console.log(`Request ${id}:`, tmpUrl.href, payload);
    const response = await fetch(tmpUrl.href, {method: 'POST', body, signal: ab.signal});
    if (response.status >= 400) throw new Error('Request failed');
    return response.json();
  }
  return apiPost(tmpUrl.href, {execute, params}, extraArgs?.timeout);
};
export function postFile(url: string, file: File): Promise<unknown> {
  const data = new FormData();
  data.append('file', file);
  return post(url, data).catch(catch401);
}
export async function postJson(
  endpoint: string,
  payload: unknown | null | undefined,
  timeout?: number,
): Promise<unknown> {
  return post(
    endpoint,
    JSON.stringify({payload}),
    {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    null,
    timeout,
  )
    .then(xhr => JSON.parse(xhr.response))
    .catch(catch401)
    .catch(({xhr, message}) => Promise.reject(message || safeJsonParse(xhr.response)));
}
function catch401(error) {
  if (error.xhr && error.xhr.status === 401 && window.location.pathname !== SIGN_IN_URL) {
    window.location.href = signInFullUrl({redirect: window.location.toLocaleString()});
    return new Promise(() => {});
  }
  return Promise.reject(error);
}
function safeJsonParse(input) {
  try {
    return JSON.parse(input);
  } catch (_e) {
    return input;
  }
}
export function postToS3(
  file: File,
  onProgress?: (this: XMLHttpRequest, ev: ProgressEvent<EventTarget>) => unknown,
  folder: string = '',
): Promise<string> {
  const url = 'https://remoov-browser-files.s3.amazonaws.com/';
  const timestamp = Date.now();
  const filename = `${folder}${timestamp}-${file.name}`;
  const formData = new FormData();
  formData.append('key', filename);
  formData.append('acl', 'public-read');
  formData.append('Content-Type', file.type);
  formData.append('file', file);

  return post(url, formData, {'Access-Control-Allow-Headers': '*'}, onProgress, 120000)
    .then(() => url + `${folder}${timestamp}-${encodeURIComponent(file.name)}`)
    .catch(({message}) => Promise.reject(message || 'Error uploading photo.'));
}
const TIMEOUT = 15000;
export function post(
  endpoint: string,
  data: FormData | string,
  headers?:
    | {
        [key: string]: string;
      }
    | null
    | undefined,
  onProgress?: ((this: XMLHttpRequest, ev: ProgressEvent<EventTarget>) => unknown) | null,
  timeout: number = TIMEOUT,
): Promise<XMLHttpRequest> {
  const id = uniqueId();
  console.log(`Request ${id}:`, endpoint, data, headers);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);
    xhr.withCredentials = endpoint.indexOf('amazonaws.com') < 0;
    if (timeout) xhr.timeout = timeout;
    xhr.onload = () => {
      if (xhr.readyState === 4) {
        if (Math.floor(xhr.status / 100) === 2) success(resolve);
        else error(reject, '');
      }
    };
    xhr.onerror = () => error(reject, 'Request failed.');
    xhr.onabort = () => error(reject, 'Request aborted.');
    xhr.ontimeout = () => error(reject, 'Request timed out.');
    if (onProgress) xhr.upload.onprogress = onProgress;
    if (headers) {
      Object.keys(headers).forEach(key => {
        if (headers) xhr.setRequestHeader(key, headers[key]);
      });
    }
    xhr.send(data);

    function success(resolve) {
      console.log(`Response ${id} (success):`, xhr.status, xhr.response, xhr);
      resolve(xhr);
    }
    function error(reject, message) {
      console.log(`Response ${id} (${message}):`, xhr.status, xhr.response, xhr);
      reject({xhr, message});
    }
  });
}
export function readExifRotation(file: File): Promise<string | null | undefined> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      // @ts-ignore
      reader.onload = () => resolve(readExifRotationFromBuffer(reader.result));
      reader.onabort = () => reject('file reading was aborted');
      reader.onerror = () => reject('file reading has failed');
      reader.readAsArrayBuffer(file);
    } catch (e: unknown) {
      reject(e);
    }
  });
}
export function loadUser(): User | null | undefined {
  const stringUser = localStorage.getItem('user');
  return stringUser ? safeParse(stringUser, null) : null;
}
export function saveUser(user: User | null | undefined): void {
  localStorage.setItem('user', JSON.stringify(user));
}
export function fileToString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    const onError = () => reject('Error loading file.');
    fileReader.onerror = onError;
    fileReader.onabort = onError;
    fileReader.onload = () => resolve(String(fileReader.result).replace(/\r/g, '\n'));
    fileReader.readAsText(file);
  });
}
export function fileOrBlobToBase64(fileOrBlob: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject('File conversion failed!');
    reader.onabort = () => reject('File conversion aborted!');
    reader.readAsDataURL(fileOrBlob);
  });
}
export function blobToJson(input: Blob): Promise<{
  [key: string]: unknown;
}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // @ts-ignore
    reader.onload = e => resolve(JSON.parse(e.target.result));
    reader.onabort = () => reject('Error converting blob to json.');
    reader.readAsText(input);
  });
}
export function remoteUrlToJson(target: string): Promise<{
  [key: string]: unknown;
}> {
  return remoteUrlToBlob(target).then(blobToJson);
}
const DOWNLOAD_FILE_TIMEOUT = 60000;
export async function remoteUrlToBlob(target: string): Promise<Blob> {
  const blob: Blob = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', target, true);
    xhr.timeout = DOWNLOAD_FILE_TIMEOUT;
    xhr.responseType = 'blob';
    xhr.onerror = () => reject('failed');
    xhr.ontimeout = () => reject('timed out');
    xhr.onabort = () => reject('aborted');
    xhr.onload = () => resolve(xhr.response);
    xhr.send(null);
  });
  return blob.size === 0 ? Promise.reject('File not found.') : blob;
}
export function fileToBlob(file: File): Promise<Blob> {
  return fileToArrayBuffer(file).then(
    ([arrayBuffer, type]) => new Blob([new Uint8Array(arrayBuffer)], {type}),
  );
  function fileToArrayBuffer(file: File): Promise<[ArrayBuffer, string]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // @ts-ignore
        const result: ArrayBuffer = reader.result;
        resolve([result, file.type]);
      };
      reader.onerror = () => reject('fileToArrayBuffer failed!');
      reader.onabort = () => reject('fileToArrayBuffer aborted!');
      reader.readAsArrayBuffer(file);
    });
  }
}
export function imageSize(imageUrl: string): Promise<{width: number; height: number}> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({width: image.width, height: image.height});
    image.onerror = () => reject(`Image download failed: '${imageUrl}'.`);
    image.onabort = () => reject(`Image download aborted: '${imageUrl}'.`);
    image.src = imageUrl;
  });
}
export function isOnline(): boolean {
  return 'onLine' in navigator && navigator.onLine;
}
export function connectionType(): string | null | undefined {
  // @ts-ignore ?
  return 'connection' in navigator ? navigator.connection.type : null;
}
export function jsonToFormData(json: {[key: string]: string | Blob}): FormData {
  const formData = new FormData();
  Object.keys(json).forEach(key => formData.append(key, json[key]));
  return formData;
}
export function base64ToBlob(base64: string): Blob {
  const byteString = atob(base64.split(',')[1]);
  const type = base64ToMimeType(base64);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const dataView = new DataView(arrayBuffer);
  for (let i = 0; i < byteString.length; i++) {
    dataView.setUint8(i, byteString.charCodeAt(i));
  }
  return new Blob([arrayBuffer], {type});
}
export function scrollPosition(scrollableElement: Element): number {
  return scrollableElement.scrollTop;
}
export function scrollTo(scrollableElement: Element, position: number): void {
  return scrollableElement.scrollTo(0, position);
}
export function wrapReactRouterRender(
  component: unknown,
  toastr?: unknown,
  customHistory?: unknown,
  api?: unknown,
  rest?: Record<string, unknown>,
) {
  return function wrappedRender({
    location,
    match,
    history,
    ...props
  }: {
    location: {search: string};
    history: unknown;
    match: {params: unknown};
  }): JSX.Element {
    const query = parseQuery(location.search || '');
    // @ts-ignore
    return createElement(component, {
      ...props,
      toastr,
      history: customHistory || history,
      match,
      params: match.params,
      location: {...location, query},
      api,
      ...rest,
    });
  };
}
export async function storageGet(key: string): Promise<unknown> {
  const value = localStorage.getItem(key);
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}
export async function storageSet(key: string, value: unknown): Promise<void> {
  localStorage.setItem(key, JSON.stringify(value));
}

export function customUseState<T>(
  initialState: T,
): [T, (t: RecursivePartial<T> | ((updatedState: T) => void)) => void] {
  const [state, setState] = useState(initialState);
  return [
    state,
    x =>
      setState(
        //@ts-ignore
        typeof x === 'function' ? x : state => mergeDeepRight(state, x),
      ),
  ];
}
export function startAmplitudeTracking(getExtraParams?: () => {[k: string]: unknown}) {
  // Amplitude tracking
  amplitude.init(AMPLITUDE_KEY).promise.then(() => {
    // Track page load
    const pageLabel = window.location.pathname
      .split(/[^a-zA-Z0-9]/)
      .filter(x => x)
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(' ');
    amplitude.track(document.body.dataset.amplitudeLabel || pageLabel, {
      ...(getExtraParams && getExtraParams()),
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      extra: document.body.dataset.amplitudeExtra,
    });

    // Track Button click
    (
      [
        ...Array.from(document.querySelectorAll('a')),
        ...Array.from(document.querySelectorAll('.amplitude-click')),
        ...Array.from(document.querySelectorAll('.w-icon-slider-left')),
        ...Array.from(document.querySelectorAll('.w-icon-slider-right')),
        ...Array.from(document.querySelectorAll('form')),
      ] as HTMLElement[]
    ).forEach((button: HTMLElement) =>
      button.addEventListener('click', () => {
        amplitude.track(button.dataset.amplitudeLabel || `${button.innerText}`, {
          ...(getExtraParams && getExtraParams()),
          url: window.location.href,
          path: window.location.pathname,
          search: window.location.search,
          buttonText: button.innerText.trim().split('\n')[0],
          buttonId: button.id,
          buttonClass: button.className,
          extra: button.dataset.amplitudeExtra,
        });
      }),
    );
    // Track Form submission
    ([...Array.from(document.querySelectorAll('form'))] as HTMLElement[]).forEach(button =>
      button.addEventListener('submit', () => {
        amplitude.track(button.dataset.amplitudeLabel || `${button.innerText}`, {
          ...(getExtraParams && getExtraParams()),
          url: window.location.href,
          path: window.location.pathname,
          search: window.location.search,
          buttonText: button.innerText,
          buttonId: button.id,
          buttonClass: button.className,
          extra: button.dataset.amplitudeExtra,
          inputs: Object.fromEntries(
            Array.from(button.querySelectorAll('input')).map(e => [
              e.dataset.amplitudeLabel || e.placeholder,
              e.value,
            ]),
          ),
        });
      }),
    );
  });
}
export const useStateMerger = <T extends Record<string, unknown>>(
  setState: Dispatch<SetStateAction<T>>,
) => {
  //@ts-ignore
  const mergeState: (state: Partial<T>) => void = compose(
    setState,
    //@ts-ignore
    mergeLeft,
  );
  const deepMergeState: (state: Partial<unknown>) => void = compose(
    setState,
    //@ts-ignore
    mergeDeepLeft,
  );
  return {mergeState, deepMergeState};
};
export function useDebounce(
  callback: () => unknown | void,
  delay: number,
  dependencies: unknown[],
) {
  useEffect(() => {
    const handler = setTimeout(callback, delay);
    return () => clearTimeout(handler);
  }, dependencies);
}

export function useThrottle(
  callback: () => unknown | void,
  delay: number,
  dependencies: unknown[],
) {
  const throttled = useCallback(throttle(delay, callback, {leading: true, trailing: true}), []);
  useEffect(() => {
    throttled();
  }, dependencies);
}
